/**
 * Datenbeschaffung der Audit-Schublade — Port von `IwmSidebarAudit.Fetch()`, erweitert um die
 * Felder der View (für die Beschriftungen) sowie Lade- und Fehlerzustand (REQ-26 §2.4).
 *
 * Der Hook lebt nur, solange die Schublade offen ist: `AuditDrawer` rendert geschlossen nichts.
 * Das Leeren der Liste in `AfterHide` ergibt sich daraus von selbst.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { containerApi } from '@/api/services/containerApi';
import { orderPositionApi } from '@/api/services/orderPositionApi';
import { receiptApi } from '@/api/services/receiptApi';
import { receiptAssignmentApi } from '@/api/services/receiptAssignmentApi';
import { viewApi } from '@/api/services/viewApi';
import type { ChinaAppSysAudit, ChinaAppSysViewField } from '@/types/entities';
import { EntityTypes } from '@/types/enums';

export interface AuditEntriesOptions {
  entity: EntityTypes;
  viewId: number;
  entryId: number;
  /** Wird gerufen, wenn die Entität keinen Audit-Dienst hat (`default`-Zweig). */
  onClose: () => void;
}

export interface AuditEntriesState {
  entries: ChinaAppSysAudit[];
  /** Felder der View; `viewApi.getFields` liefert bei einem Fehler eine leere Liste. */
  fields: ChinaAppSysViewField[];
  loading: boolean;
  /** `null`, solange kein Fehler vorliegt. */
  error: unknown;
  retry: () => void;
}

type AuditLoader = (viewId: number, id: number) => Promise<ChinaAppSysAudit[]>;

/**
 * `null` steht für den `default`-Zweig des Altclients: Die Schublade schließt sich sofort.
 * Die Rechnungszuordnung kommt hinzu — die API bietet `rechnungszuordnungen/{viewId}/{id}/audit`
 * an, der Altclient hat sie nur nicht abgefragt.
 */
function auditLoader(entity: EntityTypes): AuditLoader | null {
  switch (entity) {
    case EntityTypes.Bestellposition:
      return (viewId, id) => orderPositionApi.getAudit(viewId, id);
    case EntityTypes.Container:
      return (viewId, id) => containerApi.getAudit(viewId, id);
    case EntityTypes.Rechnung:
      return (viewId, id) => receiptApi.getAudit(viewId, id);
    case EntityTypes.Rechnungszuordnung:
      return (viewId, id) => receiptAssignmentApi.getAudit(viewId, id);
    default:
      return null;
  }
}

interface LoadedHistory {
  entries: ChinaAppSysAudit[];
  fields: ChinaAppSysViewField[];
}

/** Verlauf und Felder parallel; `async`, damit auch ein synchroner Wurf zur Ablehnung wird. */
async function loadHistory(
  load: AuditLoader,
  viewId: number,
  entryId: number,
): Promise<LoadedHistory> {
  const [entries, fields] = await Promise.all([load(viewId, entryId), viewApi.getFields(viewId)]);
  return { entries, fields };
}

const EMPTY: LoadedHistory = { entries: [], fields: [] };

export function useAuditEntries(options: AuditEntriesOptions): AuditEntriesState {
  const { entity, entryId, viewId } = options;
  const [history, setHistory] = useState<LoadedHistory>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  // Jeder neue Versuch zählt hoch und löst damit das Laden erneut aus.
  const [attempt, setAttempt] = useState(0);

  const close = useRef(options.onClose);
  close.current = options.onClose;

  useEffect(() => {
    const load = auditLoader(entity);
    if (load === null) {
      close.current();
      return;
    }

    let active = true;
    loadHistory(load, viewId, entryId).then(
      (loaded) => {
        if (!active) return;
        setHistory(loaded);
        setLoading(false);
      },
      (reason: unknown) => {
        if (!active) return;
        setError(reason ?? new Error('Fehler in GetAudit'));
        setLoading(false);
      },
    );
    return () => {
      active = false;
    };
  }, [attempt, entity, entryId, viewId]);

  const retry = useCallback(() => {
    setError(null);
    setLoading(true);
    setAttempt((current) => current + 1);
  }, []);

  return { entries: history.entries, fields: history.fields, loading, error, retry };
}
